if (!customElements.get('component-carousel')) {
  class ComponentCarousel extends HTMLElement {
    constructor() {
      super();
      this.sliderElement = this.querySelector('.keen-slider');
      this.slides = this.querySelectorAll('.keen-slider-slide');
      this.pageInfo = this.querySelector('.keen-slider-page');

      this.carouselMode = this.dataset.carouselMode || 'snap';
      this.carouselAutoplay = this.dataset.carouselAutoplay;
      this.carouselAutomove = parseFloat(this.dataset.carouselAutomove || '0');
      this.carouselLoopDesktop = this.dataset.carouselLoopDesktop;
      this.carouselLoopMobile = this.dataset.carouselLoopMobile;
      this.carouselCenteredDesktop = this.dataset.carouselCenteredDesktop;
      this.carouselCenteredTablet = this.dataset.carouselCenteredTablet;
      this.carouselCenteredMobile = this.dataset.carouselCenteredMobile;
      this.carouselSpacingDesktop = this.dataset.carouselSpacingDesktop || '20';
      this.carouselSpacingTablet = this.dataset.carouselSpacingTablet || '16';
      this.carouselSpacingMobile = this.dataset.carouselSpacingMobile || '10';
      this.slidesScrollMode = this.dataset.slidesScrollMode || 'slide';
      this.slidesPerViewDesktop = this.dataset.slidesPerViewDesktop || '3';
      this.slidesPerViewTablet = this.dataset.slidesPerViewTablet || '2.5';
      this.slidesPerViewMobile = this.dataset.slidesPerViewMobile || '2';

      this._onResize = this._onResize.bind(this);
      this._refreshScheduled = false;
      this._visibleCount = 0;

      if (this.dataset.initOndemand !== 'true') {
        setTimeout(() => {
          this.refresh();
        }, 1);
      }
    }

    connectedCallback() {
      window.addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      if (this._resizeTimer) clearTimeout(this._resizeTimer);
    }

    /**
     * Park display:none slides outside the track so Keen never measures them,
     * then destroy/reinit after layout has flushed (rAF × 2).
     */
    refresh() {
      if (this._refreshScheduled) return;
      this._refreshScheduled = true;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._refreshScheduled = false;
          this._reinitFromVisibleSlides();
        });
      });
    }

    _ensureParking() {
      if (this._parking && this.contains(this._parking)) return this._parking;
      this._parking = document.createElement('div');
      this._parking.hidden = true;
      this._parking.setAttribute('aria-hidden', 'true');
      this._parking.className = 'keen-slide-parking';
      this.appendChild(this._parking);
      return this._parking;
    }

    _syncVisibleSlides() {
      if (!this.sliderElement) return 0;
      const parking = this._ensureParking();
      const track = this.sliderElement;
      const all = [
        ...track.querySelectorAll('.keen-slider-slide'),
        ...parking.querySelectorAll('.keen-slider-slide')
      ];

      let visibleCount = 0;
      all.forEach((slide) => {
        const isHidden =
          slide.style.display === 'none' ||
          window.getComputedStyle(slide).display === 'none';

        if (isHidden) {
          slide.style.display = 'none';
          if (slide.parentElement !== parking) parking.appendChild(slide);
        } else {
          slide.style.removeProperty('display');
          if (slide.parentElement !== track) track.appendChild(slide);
          visibleCount += 1;
        }
      });

      return visibleCount;
    }

    /**
     * Resolves slidesPerView from setting vs visible slide count.
     * - Fewer slides than floor(setting): use visible count (fit)
     * - Exactly floor(setting): use integer part (e.g. 3 when setting is 3.25)
     * - More than floor(setting): use full setting if fractional (3.25), else integer
     */
    _fitSlidesPerView(visibleCount, configuredSpv) {
      const spv = parseFloat(configuredSpv) || 3;
      const baseSpv = Math.floor(spv);

      if (visibleCount < baseSpv) return visibleCount;
      if (visibleCount > baseSpv && spv % 1 !== 0) return spv;
      return baseSpv;
    }

    _hasFractionalSpv(spv) {
      return parseFloat(spv) % 1 !== 0;
    }

    _resolveLayoutMode(visibleCount) {
      const desktopSpv = parseFloat(this.slidesPerViewDesktop) || 3;
      const tabletSpv = parseFloat(this.slidesPerViewTablet) || 2.5;
      const mobileSpv = parseFloat(this.slidesPerViewMobile) || 2;

      const slidesPerViewDesktop = String(this._fitSlidesPerView(visibleCount, desktopSpv));
      const slidesPerViewTablet = String(this._fitSlidesPerView(visibleCount, tabletSpv));
      const slidesPerViewMobile = String(this._fitSlidesPerView(visibleCount, mobileSpv));

      const peekDesktop = this._hasFractionalSpv(slidesPerViewDesktop);
      const peekTablet = this._hasFractionalSpv(slidesPerViewTablet);
      const peekMobile = this._hasFractionalSpv(slidesPerViewMobile);

      return {
        centerDesktop: false,
        centerTablet: false,
        centerMobile: false,
        peekDesktop,
        peekTablet,
        peekMobile,
        useContainerDesktop: visibleCount <= Math.floor(desktopSpv),
        slidesPerViewDesktop,
        slidesPerViewTablet,
        slidesPerViewMobile
      };
    }

    _applyLayoutClasses(layout) {
      this.classList.toggle('container', !!layout.useContainerDesktop);
      this.classList.toggle('container-left', !layout.useContainerDesktop);
      this.classList.toggle('fpt__carousel--peek', !!layout.peekDesktop);
      this.dataset.effectiveCenteredDesktop = '';
    }

    _reinitFromVisibleSlides() {
      const visibleCount = this._syncVisibleSlides();
      if (visibleCount === 0) return;

      this._visibleCount = visibleCount;
      this._layout = this._resolveLayoutMode(visibleCount);
      this._applyLayoutClasses(this._layout);

      if (this.sliderObj) {
        this.sliderObj.destroy();
        this.sliderObj = null;
      }
      this.initialized = false;
      this.slides = this.sliderElement.querySelectorAll('.keen-slider-slide');
      this.initSlider();
    }

    _onResize() {
      if (this._resizeTimer) clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        if (!this.sliderObj) return;
        this.sliderObj.resize();
      }, 200);
    }

    initSlider() {
      if (!this.sliderElement || this.slides.length === 0 || this.initialized) return;
      if (typeof KeenSlider === 'undefined') return;

      this.initialized = true;

      const scrollBySlide = this.slidesScrollMode === 'slide';
      const root = this.closest('.fpt') || this;
      const layout = this._layout || this._resolveLayoutMode(this.slides.length);
      this._applyLayoutClasses(layout);

      const config = {
        mode: this.carouselMode,
        prevButton: this.querySelector('.keen-arrow.prev') || root.querySelector('.keen-arrow.prev'),
        nextButton: this.querySelector('.keen-arrow.next') || root.querySelector('.keen-arrow.next'),
        autoplay: this.carouselAutoplay,
        automove: this.carouselAutomove,
        pauseOnHover: true,
        pageInfo: this.pageInfo,
        breakpoints: {
          '(min-width: 1025px)': {
            loop: this.carouselLoopDesktop === 'true',
            centered: false,
            spacing: this.carouselSpacingDesktop,
            slidesPerView: layout.slidesPerViewDesktop,
            slidesToScroll: scrollBySlide ? 1 : Math.floor(parseFloat(layout.slidesPerViewDesktop))
          },
          '(min-width: 768px) and (max-width: 1024.98px)': {
            loop: this.carouselLoopMobile === 'true',
            centered: false,
            spacing: this.carouselSpacingTablet,
            slidesPerView: layout.slidesPerViewTablet,
            slidesToScroll: scrollBySlide ? 1 : Math.floor(parseFloat(layout.slidesPerViewTablet))
          },
          '(max-width: 767.98px)': {
            loop: this.carouselLoopMobile === 'true',
            centered: false,
            spacing: this.carouselSpacingMobile,
            slidesPerView: layout.slidesPerViewMobile,
            slidesToScroll: scrollBySlide ? 1 : Math.floor(parseFloat(layout.slidesPerViewMobile))
          }
        }
      };

      const dots = this.querySelector('.keen-dots');
      if (dots) {
        config.dots = dots;
      } else {
        config.navBar = this.querySelector('.keen-nav-bar');
        config.rubberband = false;
      }

      this.sliderObj = new KeenSlider(this.sliderElement, config);

      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', (e) => {
          const index = Array.from(this.slides).findIndex((slide) => slide === e.target);
          if (index >= 0 && this.sliderObj) {
            setTimeout(() => {
              this.sliderObj.moveToSlideRelative(index, true, 0);
            }, 10);
          }
        });
      }
    }
  }

  customElements.define('component-carousel', ComponentCarousel);
}
